import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Switch } from '../../components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { toast } from 'sonner';
import { Users, Shield, MapPin, Home, Search, Edit2, X, Check, Eye, Pencil } from 'lucide-react';
import locationsData from '../../data/locations.json';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminRoles = () => {
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingEmployee, setEditingEmployee] = useState(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  
  // Permission form state
  const [permissions, setPermissions] = useState({
    can_view: true,
    can_edit: false,
    districts: [],
    villages: []
  });
  
  // Search states
  const [districtSearch, setDistrictSearch] = useState('');
  const [villageSearch, setVillageSearch] = useState('');
  const [showDistrictDropdown, setShowDistrictDropdown] = useState(false);
  const [showVillageDropdown, setShowVillageDropdown] = useState(false);

  useEffect(() => {
    fetchEmployees();
  }, []);

  const fetchEmployees = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/roles/employees`);
      setEmployees(response.data);
    } catch (error) {
      console.error('Failed to fetch employees:', error);
      toast.error('Failed to load employees');
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (employee) => {
    setEditingEmployee(employee);
    setPermissions(employee.permissions || {
      can_view: true,
      can_edit: false,
      districts: [],
      villages: []
    });
    setDistrictSearch('');
    setVillageSearch('');
    setDialogOpen(true);
  };

  const handleSave = async () => {
    try {
      await axios.put(`${API}/roles/employees/${editingEmployee.id}/permissions`, permissions);
      toast.success('Permissions updated successfully');
      setDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to update permissions');
    }
  };

  const handleReset = async () => {
    try {
      await axios.delete(`${API}/roles/employees/${editingEmployee.id}/permissions`);
      toast.success('Permissions reset to default');
      setDialogOpen(false);
      fetchEmployees();
    } catch (error) {
      toast.error('Failed to reset permissions');
    }
  };

  // Get filtered districts
  const getFilteredDistricts = () => {
    const search = districtSearch.toLowerCase();
    const available = locationsData.districts.filter(d => !permissions.districts.includes(d));
    if (!search) return available;
    return available.filter(d => d.toLowerCase().includes(search));
  };

  // Get filtered villages (from selected districts or all)
  const getFilteredVillages = () => {
    const search = villageSearch.toLowerCase();
    let villages = [];
    
    if (permissions.districts.length > 0) {
      // Get villages from selected districts
      permissions.districts.forEach(district => {
        (locationsData.villages[district] || []).forEach(village => {
          if (!permissions.villages.includes(village)) {
            villages.push({ name: village, district });
          }
        });
      });
    } else {
      // Get all villages
      Object.entries(locationsData.villages).forEach(([district, villageList]) => {
        villageList.forEach(village => {
          if (!permissions.villages.includes(village)) {
            villages.push({ name: village, district });
          }
        });
      });
    }
    
    if (!search) return villages.slice(0, 20);
    return villages.filter(v => v.name.toLowerCase().includes(search)).slice(0, 20);
  };

  const addDistrict = (district) => {
    if (!permissions.districts.includes(district)) {
      setPermissions({
        ...permissions,
        districts: [...permissions.districts, district]
      });
    }
    setDistrictSearch('');
    setShowDistrictDropdown(false);
  };

  const removeDistrict = (district) => {
    setPermissions({
      ...permissions,
      districts: permissions.districts.filter(d => d !== district),
      // Also remove villages from this district
      villages: permissions.villages.filter(v => {
        const villageDistrict = Object.entries(locationsData.villages).find(([d, villages]) => 
          villages.includes(v)
        )?.[0];
        return villageDistrict !== district;
      })
    });
  };

  const addVillage = (village) => {
    if (!permissions.villages.includes(village)) {
      setPermissions({
        ...permissions,
        villages: [...permissions.villages, village]
      });
    }
    setVillageSearch('');
    setShowVillageDropdown(false);
  };

  const removeVillage = (village) => {
    setPermissions({
      ...permissions,
      villages: permissions.villages.filter(v => v !== village)
    });
  };

  return (
    <div className="space-y-6 animate-fade-in">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <h1 className="font-heading text-2xl font-bold tracking-tight text-foreground">
            Team Roles & Permissions
          </h1>
          <p className="font-body text-muted-foreground mt-1">
            Assign districts and villages to your team members
          </p>
        </div>
      </div>

      {/* Employee Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {loading ? (
          <div className="col-span-full flex justify-center py-12">
            <div className="w-8 h-8 border-4 border-brand-gold border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : employees.length === 0 ? (
          <div className="col-span-full text-center py-12">
            <Users className="w-12 h-12 text-muted-foreground mx-auto mb-2" />
            <p className="font-body text-muted-foreground">No employees found</p>
          </div>
        ) : (
          employees.map((employee) => (
            <Card key={employee.id} className="rounded-none border-border" data-testid={`employee-card-${employee.id}`}>
              <CardHeader className="pb-3">
                <div className="flex items-start justify-between">
                  <div>
                    <CardTitle className="font-heading text-lg">{employee.name}</CardTitle>
                    <p className="font-body text-sm text-muted-foreground">{employee.email}</p>
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleEdit(employee)}
                    className="rounded-none"
                    data-testid={`edit-permissions-${employee.id}`}
                  >
                    <Edit2 className="w-4 h-4" />
                  </Button>
                </div>
              </CardHeader>
              <CardContent className="space-y-3">
                {/* Permissions Display */}
                <div className="flex items-center gap-4">
                  <div className="flex items-center gap-2">
                    <Eye className={`w-4 h-4 ${employee.permissions?.can_view ? 'text-green-500' : 'text-muted-foreground'}`} />
                    <span className="font-body text-xs">View</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <Pencil className={`w-4 h-4 ${employee.permissions?.can_edit ? 'text-green-500' : 'text-muted-foreground'}`} />
                    <span className="font-body text-xs">Edit</span>
                  </div>
                </div>

                {/* Districts */}
                <div>
                  <p className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Districts</p>
                  <div className="flex flex-wrap gap-1">
                    {employee.permissions?.districts?.length > 0 ? (
                      employee.permissions.districts.map(district => (
                        <Badge key={district} className="rounded-none bg-brand-gold/20 text-brand-brown text-[10px]">
                          <MapPin className="w-3 h-3 mr-1" />
                          {district}
                        </Badge>
                      ))
                    ) : (
                      <span className="font-body text-xs text-muted-foreground">All districts</span>
                    )}
                  </div>
                </div>

                {/* Villages */}
                <div>
                  <p className="font-heading text-[10px] uppercase tracking-widest text-muted-foreground mb-1">Villages</p>
                  <div className="flex flex-wrap gap-1">
                    {employee.permissions?.villages?.length > 0 ? (
                      employee.permissions.villages.slice(0, 3).map(village => (
                        <Badge key={village} className="rounded-none bg-secondary text-foreground text-[10px]">
                          <Home className="w-3 h-3 mr-1" />
                          {village}
                        </Badge>
                      ))
                    ) : (
                      <span className="font-body text-xs text-muted-foreground">All villages</span>
                    )}
                    {employee.permissions?.villages?.length > 3 && (
                      <Badge className="rounded-none bg-secondary text-foreground text-[10px]">
                        +{employee.permissions.villages.length - 3} more
                      </Badge>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))
        )}
      </div>

      {/* Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto" aria-describedby="permissions-dialog-description">
          <DialogHeader>
            <DialogTitle className="font-heading text-xl font-bold flex items-center gap-2">
              <Shield className="w-5 h-5 text-brand-gold" />
              Edit Permissions
            </DialogTitle>
            <p id="permissions-dialog-description" className="font-body text-sm text-muted-foreground">
              {editingEmployee?.name} - {editingEmployee?.email}
            </p>
          </DialogHeader>

          <div className="space-y-6 mt-4">
            {/* View/Edit Permissions */}
            <div className="space-y-4 p-4 bg-secondary/30 border border-border">
              <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                Access Permissions
              </h4>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Eye className="w-4 h-4 text-muted-foreground" />
                  <Label className="font-body">Can View Listings</Label>
                </div>
                <Switch
                  checked={permissions.can_view}
                  onCheckedChange={(checked) => setPermissions({ ...permissions, can_view: checked })}
                  data-testid="permission-view"
                />
              </div>
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <Pencil className="w-4 h-4 text-muted-foreground" />
                  <Label className="font-body">Can Edit Listings</Label>
                </div>
                <Switch
                  checked={permissions.can_edit}
                  onCheckedChange={(checked) => setPermissions({ ...permissions, can_edit: checked })}
                  data-testid="permission-edit"
                />
              </div>
            </div>

            {/* District Selection */}
            <div className="space-y-3">
              <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                Assigned Districts
              </h4>
              <p className="font-body text-xs text-muted-foreground">
                Leave empty for access to all districts
              </p>
              
              {/* Selected Districts */}
              {permissions.districts.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2">
                  {permissions.districts.map(district => (
                    <Badge key={district} className="rounded-none bg-brand-gold/20 text-brand-brown flex items-center gap-1">
                      <MapPin className="w-3 h-3" />
                      {district}
                      <button onClick={() => removeDistrict(district)} className="ml-1 hover:bg-brand-brown/20 rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* District Search */}
              <div className="relative">
                <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-brand-gold" />
                <Input
                  placeholder="Search and add district..."
                  value={districtSearch}
                  onChange={(e) => {
                    setDistrictSearch(e.target.value);
                    setShowDistrictDropdown(true);
                  }}
                  onFocus={() => setShowDistrictDropdown(true)}
                  onBlur={() => setTimeout(() => setShowDistrictDropdown(false), 200)}
                  className="rounded-none border-2 pl-10"
                  data-testid="district-search"
                />
                
                {showDistrictDropdown && getFilteredDistricts().length > 0 && (
                  <div className="absolute top-full left-0 right-0 bg-white border-2 border-t-0 border-border shadow-lg z-50 max-h-48 overflow-y-auto">
                    {getFilteredDistricts().map(district => (
                      <button
                        key={district}
                        type="button"
                        onClick={() => addDistrict(district)}
                        className="w-full px-3 py-2 text-left hover:bg-secondary/50 flex items-center gap-2 border-b border-border/50 last:border-b-0"
                      >
                        <MapPin className="w-3 h-3 text-brand-gold" />
                        <span className="font-body text-sm">{district}</span>
                      </button>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* Village Selection */}
            <div className="space-y-3">
              <h4 className="font-heading text-xs uppercase tracking-widest font-bold text-muted-foreground">
                Assigned Villages
              </h4>
              <p className="font-body text-xs text-muted-foreground">
                Leave empty for access to all villages in assigned districts
              </p>
              
              {/* Selected Villages */}
              {permissions.villages.length > 0 && (
                <div className="flex flex-wrap gap-2 mb-2 max-h-24 overflow-y-auto">
                  {permissions.villages.map(village => (
                    <Badge key={village} className="rounded-none bg-secondary text-foreground flex items-center gap-1 text-xs">
                      <Home className="w-3 h-3" />
                      {village}
                      <button onClick={() => removeVillage(village)} className="ml-1 hover:bg-muted rounded-full p-0.5">
                        <X className="w-3 h-3" />
                      </button>
                    </Badge>
                  ))}
                </div>
              )}
              
              {/* Village Search */}
              <div className="relative">
                <Home className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search and add village..."
                  value={villageSearch}
                  onChange={(e) => {
                    setVillageSearch(e.target.value);
                    setShowVillageDropdown(true);
                  }}
                  onFocus={() => setShowVillageDropdown(true)}
                  onBlur={() => setTimeout(() => setShowVillageDropdown(false), 200)}
                  className="rounded-none border-2 pl-10"
                  data-testid="village-search"
                />
                
                {showVillageDropdown && villageSearch && (
                  <div className="absolute top-full left-0 right-0 bg-white border-2 border-t-0 border-border shadow-lg z-50 max-h-48 overflow-y-auto">
                    {getFilteredVillages().length === 0 ? (
                      <div className="px-3 py-2 text-sm text-muted-foreground">No villages found</div>
                    ) : (
                      getFilteredVillages().map((item, idx) => (
                        <button
                          key={`${item.name}-${idx}`}
                          type="button"
                          onClick={() => addVillage(item.name)}
                          className="w-full px-3 py-2 text-left hover:bg-secondary/50 flex items-center gap-2 border-b border-border/50 last:border-b-0"
                        >
                          <Home className="w-3 h-3 text-muted-foreground" />
                          <span className="font-body text-sm">{item.name}</span>
                          <span className="font-body text-xs text-muted-foreground ml-auto">{item.district}</span>
                        </button>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div className="flex gap-3 pt-4 border-t border-border">
              <Button
                onClick={handleSave}
                className="flex-1 rounded-none font-heading font-bold uppercase tracking-wide bg-brand-gold text-brand-brown hover:bg-brand-gold/90"
                data-testid="save-permissions"
              >
                <Check className="w-4 h-4 mr-2" />
                Save Permissions
              </Button>
              <Button
                variant="outline"
                onClick={handleReset}
                className="rounded-none border-2"
                data-testid="reset-permissions"
              >
                Reset
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminRoles;
